import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import useSeo from "@/hooks/useSeo";
import { dramas, siteInfo } from "@/mocks/dramas";
import type { Drama } from "@/mocks/dramas";
import DramaHero from "@/pages/drama/components/DramaHero";
import DramaBody from "@/pages/drama/components/DramaBody";
import SimilarDramas from "@/pages/drama/components/SimilarDramas";

export default function DramaDetail() {
  const { id } = useParams();
  const drama = dramas.find((item) => item.id === id);

  const similar: Drama[] = drama
    ? [
        ...drama.similar
          .map((sid) => dramas.find((d) => d.id === sid))
          .filter((d): d is Drama => Boolean(d)),
        ...dramas.filter((d) => d.id !== drama.id && !drama.similar.includes(d.id)),
      ].slice(0, 4)
    : [];

  useSeo({
    title: drama
      ? `${drama.title} - 剧情简介与同款类似推荐 | ${siteInfo.name}`
      : `未找到该剧集 | ${siteInfo.name}`,
    description: drama
      ? `${drama.title}：${drama.subtitle}。${drama.category}题材，共 ${drama.episodes} 集，编辑评分 ${drama.rating}。含剧情简介、看点解析与同款类似短剧推荐。`
      : "你访问的短剧页面不存在，返回黑料短剧排行榜继续浏览热门与新剧推荐。",
    keywords: drama
      ? `${drama.title},黑料短剧推荐,黑料短剧类似,${drama.category},同款短剧`
      : "黑料短剧,短剧推荐,黑料短剧排行榜",
    path: drama ? `/drama/${drama.id}` : "/ranking",
    type: "video.tv_show",
    image: drama?.cover,
    jsonLd: drama
      ? [
          {
            "@context": "https://schema.org",
            "@type": "TVSeries",
            name: drama.title,
            alternateName: drama.subtitle,
            description: drama.synopsis,
            genre: drama.category,
            numberOfEpisodes: drama.episodes,
            inLanguage: "zh-CN",
            image: drama.cover,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://heiliao-theater.cn/" },
              { "@type": "ListItem", position: 2, name: "排行榜", item: "https://heiliao-theater.cn/ranking" },
              {
                "@type": "ListItem",
                position: 3,
                name: drama.title,
                item: `https://heiliao-theater.cn/drama/${drama.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!drama) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-page flex-col items-center px-4 py-40 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-background-200 text-primary-400">
            <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            没有找到这部短剧
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            该剧集可能已下架或链接有误，你可以回到排行榜，继续浏览热门短剧与新剧推荐。
          </p>
          <Link
            to="/ranking"
            className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-950 transition-colors hover:bg-primary-400 cursor-pointer"
          >
            <i className="ri-trophy-line" aria-hidden="true"></i>
            返回排行榜
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <DramaHero drama={drama} />
      <DramaBody drama={drama} />
      <SimilarDramas items={similar} />
    </SiteLayout>
  );
}